/**
 * Dropdown menu (Radix `DropdownMenu` behaviour on shadcn markup).
 *
 * A button opens a menu of actions. Opening, focus, scroll lock, `inert`,
 * outside-press and Escape come from the layer spine; everything inside the
 * menu (roving focus, typeahead, checkbox/radio items, submenus) comes from
 * `menu-core`.
 *
 * @markup dropdown-menu div [ui-dropdown-menu] style="display: contents" data-state=open|closed
 * @markup dropdown-menu-trigger button [ui-dropdown-menu-trigger] aria-haspopup=menu aria-expanded aria-controls data-state=open|closed
 * @markup dropdown-menu-portal div [ui-dropdown-menu-portal] style="display: contents"
 * @markup dropdown-menu-content div [ui-dropdown-menu-content] role=menu hidden data-state=open|closed data-side data-align data-prefer-side? data-prefer-align? data-side-offset? data-align-offset? (--anchor-width|height, --available-width|height, --transform-origin added)
 * @markup dropdown-menu-group div [ui-dropdown-menu-group] role=group
 * @markup dropdown-menu-label div [ui-dropdown-menu-label] data-inset?
 * @markup dropdown-menu-item div [ui-dropdown-menu-item] role=menuitem tabindex=-1 data-disabled? data-variant=default|destructive data-inset?
 * @markup dropdown-menu-checkbox-item div [ui-dropdown-menu-checkbox-item] role=menuitemcheckbox aria-checked data-state=checked|unchecked
 * @markup dropdown-menu-item-indicator span [ui-dropdown-menu-item-indicator] presentational; the check/dot slot of a checkbox or radio item, holding a `data-menu-item-indicator` child that is shown only while checked
 * @markup dropdown-menu-radio-group div [ui-dropdown-menu-radio-group] role=group data-value?
 * @markup dropdown-menu-radio-item div [ui-dropdown-menu-radio-item] role=menuitemradio aria-checked data-state=checked|unchecked data-value
 * @markup dropdown-menu-separator div [ui-dropdown-menu-separator] role=separator
 * @markup dropdown-menu-shortcut span [ui-dropdown-menu-shortcut]
 * @markup dropdown-menu-sub div [ui-dropdown-menu-sub]
 * @markup dropdown-menu-sub-trigger div [ui-dropdown-menu-sub-trigger] data-inset? role=menuitem aria-haspopup=menu aria-expanded data-state=open|closed
 * @markup dropdown-menu-sub-content div [ui-dropdown-menu-sub-content] role=menu hidden data-state=open|closed data-side data-align
 *
 * Content reads its preferred placement from `data-prefer-side`, `data-prefer-align`,
 * `data-side-offset`, `data-align-offset`, `data-collision-padding` and
 * `data-avoid-collisions="false"` (each overridden by the matching option);
 * `data-side`/`data-align` report the resolved, possibly flipped, placement.
 *
 * Custom properties on the content (and each sub-content), published by the
 * shared floating layer (`_floating.ts`): `--anchor-width`, `--anchor-height`,
 * `--available-width`, `--available-height`, `--transform-origin`.
 *
 * @module dropdown-menu
 */
import { partOf, partTarget, setState } from './_dom.js';
import { createOpenState, ensureId, mountFloating, openController, readPlacement, } from './_floating.js';
import { attachMenuEngine, resolveDirection, } from './menu-core.js';
const NAME = 'dropdown-menu';
export function attachDropdownMenu(root, options = {}) {
    const triggerPart = partOf(root, NAME, `${NAME}-trigger`);
    const contentPart = partOf(root, NAME, `${NAME}-content`);
    if (!triggerPart || !contentPart) {
        throw new Error(`attachDropdownMenu: root needs ${NAME}-trigger and ${NAME}-content parts`);
    }
    const content = contentPart;
    const trigger = partTarget(triggerPart);
    const modal = options.modal ?? root.getAttribute('data-modal') !== 'false';
    const dir = () => options.dir ?? resolveDirection(trigger);
    let destroyed = false;
    let floating = null;
    let engine = null;
    let nextSource = 'api';
    let pointerHandled = false;
    let keyHandled = false;
    const states = (state) => {
        setState(state, root, triggerPart, trigger, content);
        trigger.setAttribute('aria-expanded', String(state === 'open'));
    };
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-controls', ensureId(content, 'dropdown-menu-content'));
    content.setAttribute('aria-labelledby', ensureId(trigger, 'dropdown-menu-trigger'));
    content.setAttribute('role', 'menu');
    content.hidden = true;
    states('closed');
    function show() {
        const source = nextSource;
        nextSource = 'api';
        // Reopening while the previous exit animation still runs: start clean.
        engine?.destroy();
        floating?.destroy();
        states('open');
        floating = mountFloating({
            content,
            anchor: trigger,
            modal,
            trapFocus: modal,
            dismissOnFocusOutside: !modal,
            initialFocus: false,
            isInside: (node) => trigger.contains(node),
            onDismiss: () => state.request(false),
            placement: readPlacement(content, {
                side: options.side,
                align: options.align,
                sideOffset: options.sideOffset,
                alignOffset: options.alignOffset,
                avoidCollisions: options.avoidCollisions,
                collisionPadding: options.collisionPadding,
            }, {
                side: 'bottom',
                align: 'start',
                sideOffset: 4,
                rtl: dir() === 'rtl',
            }),
        });
        engine = attachMenuEngine({
            prefix: NAME,
            content,
            dir,
            requestClose: () => state.request(false),
            ...(options.loop === undefined ? {} : { loop: options.loop }),
            ...(options.closeOnSelect === undefined ? {} : { closeOnSelect: options.closeOnSelect }),
            ...(options.typeaheadTimeout === undefined
                ? {}
                : { typeaheadTimeout: options.typeaheadTimeout }),
            ...(options.subOpenDelay === undefined ? {} : { subOpenDelay: options.subOpenDelay }),
            ...(options.onSelect ? { onSelect: options.onSelect } : {}),
            ...(options.onCheckedChange ? { onCheckedChange: options.onCheckedChange } : {}),
            ...(options.onValueChange ? { onValueChange: options.onValueChange } : {}),
        });
        const event = new Event('dropdown-menu-open-autofocus', { cancelable: true });
        options.onOpenAutoFocus?.(event);
        if (event.defaultPrevented)
            return;
        if (source === 'keyboard')
            engine.highlight('first');
        else if (source === 'keyboard-last')
            engine.highlight('last');
        else
            content.focus({ preventScroll: true });
    }
    function hide() {
        engine?.closeSubmenus();
        states('closed');
        floating?.close(() => {
            engine?.destroy();
            engine = null;
            floating = null;
            const active = content.ownerDocument.activeElement;
            // Do not pull focus back from somewhere the selection just sent it.
            if (active && active !== content.ownerDocument.body && !content.contains(active))
                return;
            const event = new Event('dropdown-menu-close-autofocus', { cancelable: true });
            options.onCloseAutoFocus?.(event);
            if (!event.defaultPrevented)
                trigger.focus({ preventScroll: true });
        });
    }
    const state = createOpenState(options, (open) => {
        if (open)
            show();
        else
            hide();
    });
    const request = (next, source) => {
        nextSource = source;
        state.request(next);
        nextSource = 'api';
    };
    const triggerDisabled = () => trigger.hasAttribute('disabled') ||
        (trigger.hasAttribute('data-disabled') && trigger.getAttribute('data-disabled') !== 'false');
    function onPointerDown(event) {
        if (triggerDisabled() || event.button !== 0 || event.ctrlKey)
            return;
        pointerHandled = true;
        setTimeout(() => {
            pointerHandled = false;
        }, 0);
        const wasOpen = state.isOpen;
        request(!wasOpen, 'pointer');
        // Keep the trigger from taking focus so the opened menu can.
        if (!wasOpen)
            event.preventDefault();
    }
    function onClick() {
        if (triggerDisabled())
            return;
        if (pointerHandled || keyHandled) {
            pointerHandled = false;
            return;
        }
        request(!state.isOpen, 'keyboard');
    }
    function onKeyDown(event) {
        if (triggerDisabled())
            return;
        const { key } = event;
        if (key === 'Enter' || key === ' ') {
            keyHandled = true;
            event.preventDefault();
            request(!state.isOpen, 'keyboard');
        }
        else if (key === 'ArrowDown') {
            event.preventDefault();
            if (state.isOpen)
                engine?.highlight('first');
            else
                request(true, 'keyboard');
        }
        else if (key === 'ArrowUp') {
            event.preventDefault();
            if (state.isOpen)
                engine?.highlight('last');
            else
                request(true, 'keyboard-last');
        }
    }
    function onKeyUp() {
        setTimeout(() => {
            keyHandled = false;
        }, 0);
    }
    trigger.addEventListener('pointerdown', onPointerDown);
    trigger.addEventListener('click', onClick);
    trigger.addEventListener('keydown', onKeyDown);
    trigger.addEventListener('keyup', onKeyUp);
    if (state.isOpen)
        show();
    const base = openController(state, () => {
        if (destroyed)
            return;
        destroyed = true;
        trigger.removeEventListener('pointerdown', onPointerDown);
        trigger.removeEventListener('click', onClick);
        trigger.removeEventListener('keydown', onKeyDown);
        trigger.removeEventListener('keyup', onKeyUp);
        engine?.destroy();
        engine = null;
        floating?.destroy();
        floating = null;
        states('closed');
    });
    return {
        open: base.open,
        close: base.close,
        toggle: base.toggle,
        update: base.update,
        destroy: base.destroy,
        get isOpen() {
            return base.isOpen;
        },
        content,
        trigger,
    };
}
